import React, { forwardRef, useId } from 'react'

import clsx from 'clsx'

import WarningErrorIcon from '@core/components/icons/WarningErrorIcon'
import './ApplyV2InputText.scss'

interface Props {
    value?: string | number | boolean | null | undefined
    label?: string
    onChange?: React.ChangeEventHandler<HTMLInputElement>
    onFocus?: React.FocusEventHandler<HTMLInputElement>
    onBlur?: React.FocusEventHandler<HTMLInputElement>
    onInput?: (e: React.FormEvent<HTMLInputElement>) => void
    isDisabled?: boolean
    className?: string
    id?: string | number
    name?: string
    isLoading?: boolean
    errorMessage?: string | null | boolean
    children?: React.ReactNode
    isRequired?: boolean
    placeholder?: string
    maxLength?: number
    minLength?: number
    autoFocus?: boolean
    autoComplete?: string
    isShowErrorMessage?: boolean
    description?: React.ReactNode
    inputMode?: 'text' | 'email' | 'search' | 'tel' | 'none' | 'url' | 'numeric' | 'decimal' | undefined
}

const baseClass = 'ApplyV2InputText'

const ApplyV2InputText = forwardRef<HTMLInputElement, Props>(function ApplyV2InputText(
    {
        value,
        label,
        onChange,
        onFocus,
        onBlur,
        onInput,
        isDisabled = false,
        className = '',
        id,
        name,
        isLoading = false,
        errorMessage,
        children,
        isRequired = false,
        placeholder = '',
        maxLength,
        minLength,
        autoFocus,
        autoComplete,
        isShowErrorMessage = true,
        description,
        inputMode = 'text',
    }: Props,
    ref
) {
    const defaultId = useId()


    return (
        <div
            className={clsx(baseClass, className, {
                [`${baseClass}--error`]: !!errorMessage,
                [`${baseClass}--disabled`]: isDisabled,
                [`${baseClass}--loading`]: isLoading,
            })}
            data-error={!!errorMessage ? 'true' : 'false'}
        >
            {label && (
                <label className={`${baseClass}__label`} htmlFor={id ? id.toString() : defaultId}>
                    {label}
                    {/*{isRequired && <span className={`${baseClass}__required`}>*</span>}*/}
                    {isRequired && '*'}
                </label>
            )}
            <div
                className={clsx(`${baseClass}__input-wrapper`, {
                    [`${className}__input-wrapper`]: className,
                    [`${baseClass}__input-wrapper--disabled`]: isDisabled,
                    [`${baseClass}__input-wrapper--loading`]: isLoading,
                    [`${baseClass}__input-wrapper--error`]: !!errorMessage,
                    [`${className}__input-wrapper--disabled`]: className && isDisabled,
                    [`${className}__input-wrapper--loading`]: className && isLoading,
                    [`${className}__input-wrapper--error`]: className && !!errorMessage,
                })}
            >
                <input
                    ref={ref}
                    id={id ? id.toString() : defaultId}
                    name={name}
                    value={value?.toString() ?? ''}
                    onChange={onChange}
                    onInput={onInput}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    disabled={isDisabled}
                    placeholder={placeholder}
                    maxLength={maxLength}
                    minLength={minLength}
                    autoFocus={autoFocus}
                    autoComplete={autoComplete}
                    className={clsx(`${baseClass}__input`, {
                        [`${baseClass}__input--error`]: !!errorMessage,
                        [`${baseClass}__input--loading`]: isLoading,
                        [`${className}__input--error`]: className && !!errorMessage,
                        [`${className}__input--loading`]: className && isLoading,
                    })}
                    inputMode={inputMode}
                />
            </div>
            {description && (
                <div
                    className={clsx(`${baseClass}__description`, {
                        [`${className}__description`]: className,
                    })}
                >
                    {description}
                </div>
            )}
            {errorMessage && isShowErrorMessage && (
                <div
                    className={clsx(`${baseClass}__error`, {
                        [`${className}__error`]: className,
                    })}
                >
                    <div
                        className={clsx(`${baseClass}__error--icon`, {
                            [`${className}__error--icon`]: className,
                        })}
                    >
                        {/*<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">*/}
                        {/*    <g clipPath="url(#clip0_4800_38853)">*/}
                        {/*        <path*/}
                        {/*            fillRule="evenodd"*/}
                        {/*            clipRule="evenodd"*/}
                        {/*            d="M8.00006 1.9334C4.64954 1.9334 1.9334 4.64954 1.9334 8.00006C1.9334 11.3506 4.64954 14.0667 8.00006 14.0667C11.3506 14.0667 14.0667 11.3506 14.0667 8.00006C14.0667 4.64954 11.3506 1.9334 8.00006 1.9334ZM0.733398 8.00006C0.733398 3.9868 3.9868 0.733398 8.00006 0.733398C12.0133 0.733398 15.2667 3.9868 15.2667 8.00006C15.2667 12.0133 12.0133 15.2667 8.00006 15.2667C3.9868 15.2667 0.733398 12.0133 0.733398 8.00006ZM8.00006 9.26673C7.66869 9.26673 7.40007 8.9981 7.40007 8.66673V4.66673C7.40007 4.33536 7.66869 4.06673 8.00006 4.06673C8.33144 4.06673 8.60006 4.33536 8.60006 4.66673V8.66673C8.60006 8.9981 8.33144 9.26673 8.00006 9.26673ZM8.00006 11.3334C8.36826 11.3334 8.66673 11.0349 8.66673 10.6667C8.66673 10.2985 8.36826 10.0001 8.00006 10.0001C7.63188 10.0001 7.3334 10.2985 7.3334 10.6667C7.3334 11.0349 7.63188 11.3334 8.00006 11.3334Z"*/}
                        {/*            fill="#B62626"*/}
                        {/*        />*/}
                        {/*    </g>*/}
                        {/*    <defs>*/}
                        {/*        <clipPath id="clip0_4800_38853">*/}
                        {/*            <rect width="16" height="16" fill="white" />*/}
                        {/*        </clipPath>*/}
                        {/*    </defs>*/}
                        {/*</svg>*/}
                        <WarningErrorIcon />
                    </div>
                    <div
                        className={clsx(`${baseClass}__error--text`, {
                            [`${className}__error--text`]: className,
                        })}
                    >
                        {errorMessage}
                    </div>
                </div>
            )}
        </div>
    )
})

export default ApplyV2InputText
